/**
 * useConfirm — promise-returning confirmation dialog.
 *
 * Replaces native `window.confirm()` with the editorial-styled
 * <ConfirmDialog /> mounted once per portal.
 *
 * Usage:
 *   const { confirm } = useConfirm()
 *   if (!await confirm({
 *     title: 'Delete category',
 *     message: `Delete "${name}" and all its responses?`,
 *     confirmLabel: 'Delete',
 *     tone: 'danger',
 *   })) return
 *   await service.delete(id)
 *
 * The composable holds module-level state. Any view can call
 * `confirm(...)`; the single mounted <ConfirmDialog /> renders the
 * prompt and resolves the returned promise with true (confirm) or
 * false (cancel / backdrop click / Escape).
 */
import { ref } from 'vue';

export interface ConfirmOptions {
  /** Title shown in the modal head. Required. */
  title: string;
  /** Body text — supports plain string. Required. */
  message: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Visual tone — drives icon + button color. Default 'default'. */
  tone?: 'danger' | 'default';
  /** Material icon name override. Defaults: 'warning' (danger), 'help' (default). */
  icon?: string;
  /** Optional sub-title under the main title. */
  subtitle?: string;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve: ((value: boolean) => void) | null;
}

// Module-level singleton state — one prompt at a time per portal.
const state = ref<ConfirmState>({
  open: false,
  title: '',
  message: '',
  resolve: null,
});

function answer(value: boolean) {
  const resolver = state.value.resolve;
  state.value = { ...state.value, open: false, resolve: null };
  if (resolver) resolver(value);
}

export function useConfirm() {
  /**
   * Show a confirm dialog. Resolves true if the user confirms, false
   * if they cancel, click the backdrop, or press Escape.
   */
  function confirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      // If another dialog is already open, resolve it as cancelled
      // before showing the new one. Prevents promise leaks if a view
      // triggers two confirms in quick succession.
      if (state.value.resolve) state.value.resolve(false);
      state.value = {
        title: opts.title,
        message: opts.message,
        confirmLabel: opts.confirmLabel ?? 'Confirm',
        cancelLabel: opts.cancelLabel ?? 'Cancel',
        tone: opts.tone ?? 'default',
        icon: opts.icon,
        subtitle: opts.subtitle,
        open: true,
        resolve,
      };
    });
  }

  return { confirm, state, answer };
}
